import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { coursesApi } from "../lib";
import { showError } from "../lib/toast";
import {
  PlayCircle,
  Home,
  CheckCircle,
  Sparkles,
  Trophy,
  Star,
  Gift,
  PartyPopper,
  Rocket,
  Heart,
  Award,
  Crown,
} from "lucide-react";

const EnrollmentSuccessPage = () => {
  const location = useLocation() as any;
  const navigate = useNavigate();
  const [course, setCourse] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showConfetti, setShowConfetti] = useState(true);
  const [fireworks, setFireworks] = useState<{ id: number; x: number; y: number }[]>([]);

  useEffect(() => {
    const loadCourse = async () => {
      if (location.state && location.state.course) {
        setCourse(location.state.course);
        setLoading(false);
        return;
      }

      const pathname = location.pathname;
      const courseId = pathname.split("/")[2];

      if (!courseId) {
        setError("Invalid course ID");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await coursesApi.getCourseById(courseId);
        if (response.success && response.data) {
          setCourse(response.data);
        } else {
          throw new Error("Course not found");
        }
      } catch (err: any) {
        setError(err.message || "Failed to load course details");
        showError(err.message || "Failed to load course details");
      } finally {
        setLoading(false);
      }
    };

    loadCourse();

    // Generate random fireworks (reduced for mobile performance)
    const interval = setInterval(() => {
      if (showConfetti) {
        const newFirework = {
          id: Date.now() + Math.random(),
          x: Math.random() * 100,
          y: Math.random() * 60 + 20,
        };
        setFireworks(prev => [...prev.slice(-5), newFirework]);
        setTimeout(() => {
          setFireworks(prev => prev.filter(f => f.id !== newFirework.id));
        }, 1000);
      }
    }, 2000);

    // Auto-hide confetti after 6 seconds
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 6000);

    return () => {
      clearInterval(interval);
      clearTimeout(timer);
    };
  }, [location, showConfetti]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
        <div className="px-4 text-center">
          <div className="relative">
            <div className="w-12 h-12 mx-auto mb-3 border-4 rounded-full sm:w-16 sm:h-16 sm:mb-4 border-primary border-t-transparent animate-spin" />
            <Sparkles className="absolute w-5 h-5 -translate-x-1/2 -translate-y-1/2 sm:w-6 sm:h-6 text-primary top-1/2 left-1/2 animate-pulse" />
          </div>
          <p className="text-xs text-gray-600 sm:text-sm">Loading your enrollment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4 bg-gradient-to-br from-red-50 to-orange-50">
        <div className="w-full max-w-md p-5 text-center bg-white shadow-xl sm:p-8 rounded-2xl">
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-3 bg-red-100 rounded-full sm:w-20 sm:h-20 sm:mb-4">
            <svg className="w-8 h-8 text-red-500 sm:w-10 sm:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="mb-2 text-lg font-bold text-gray-900 sm:text-xl">Something went wrong</h2>
          <p className="mb-5 text-xs text-gray-600 sm:text-sm sm:mb-6">{error}</p>
          <div className="flex flex-col justify-center gap-2 sm:flex-row sm:gap-3">
            <button
              onClick={() => navigate("/courses")}
              className="px-4 py-2 sm:px-5 sm:py-2.5 bg-primary text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors"
            >
              Browse Courses
            </button>
            <button
              onClick={() => navigate("/")}
              className="px-4 py-2 sm:px-5 sm:py-2.5 bg-gray-200 text-gray-800 text-xs sm:text-sm font-medium rounded-lg hover:bg-gray-300 transition-colors"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      {/* Animated Background - Reduced opacity for mobile */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute bg-green-200 rounded-full -top-40 -right-40 w-60 h-60 sm:w-80 sm:h-80 opacity-20 animate-pulse" />
        <div className="absolute delay-1000 rounded-full -bottom-40 -left-40 w-60 h-60 sm:w-80 sm:h-80 bg-emerald-200 opacity-20 animate-pulse" />
        <div className="absolute w-64 h-64 -translate-x-1/2 -translate-y-1/2 bg-teal-200 rounded-full top-1/2 left-1/2 sm:w-96 sm:h-96 opacity-10 animate-pulse delay-2000" />
      </div>

      {/* Confetti Particles - Reduced count for mobile performance */}
      {showConfetti && (
        <div className="fixed inset-0 z-10 pointer-events-none">
          {[...Array(80)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${Math.random() * 6 + 3}px`,
                height: `${Math.random() * 6 + 3}px`,
                backgroundColor: `hsl(${Math.random() * 360}, 80%, 60%)`,
                borderRadius: Math.random() > 0.5 ? "50%" : "0",
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
                opacity: 0.7,
                transform: `rotate(${Math.random() * 360}deg)`,
              }}
            />
          ))}
        </div>
      )}

      {/* Floating Icons - Reduced count for mobile */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(12)].map((_, i) => {
          const icons = [Star, Sparkles, Trophy, Gift, PartyPopper, Rocket, Heart, Award];
          const Icon = icons[Math.floor(Math.random() * icons.length)];
          return (
            <div
              key={i}
              className="absolute animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 4}s`,
                animationDuration: `${3 + Math.random() * 3}s`,
              }}
            >
              <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary/20" />
            </div>
          );
        })}
      </div>

      {/* Fireworks - Optimized for mobile */}
      {fireworks.map((firework) => (
        <div
          key={firework.id}
          className="fixed z-20 pointer-events-none animate-firework"
          style={{
            left: `${firework.x}%`,
            top: `${firework.y}%`,
          }}
        >
          <div className="relative">
            <div className="absolute w-1.5 h-1.5 bg-yellow-400 rounded-full" />
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="absolute w-0.5 h-0.5 rounded-full animate-explode"
                style={{
                  backgroundColor: `hsl(${i * 45}, 80%, 60%)`,
                  transform: `rotate(${i * 45}deg) translateX(12px)`,
                  animationDelay: `${i * 0.05}s`,
                }}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Main Content - Fully Responsive */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-8 sm:px-6 sm:py-12">
        <div className="w-full max-w-2xl mx-auto space-y-6 text-center sm:space-y-8">
          {/* Animated Success Icon - Responsive sizing */}
          <div className="relative">
            <div className="absolute inset-0 animate-ping">
              <div className="w-20 h-20 mx-auto bg-green-400 rounded-full sm:w-24 sm:h-24 opacity-30" />
            </div>
            <div className="absolute inset-0 delay-300 animate-pulse">
              <div className="w-20 h-20 mx-auto bg-green-300 rounded-full sm:w-24 sm:h-24 opacity-20" />
            </div>
            <div className="relative flex items-center justify-center w-20 h-20 mx-auto rounded-full shadow-2xl sm:w-24 sm:h-24 bg-gradient-to-br from-green-500 to-emerald-600 animate-bounce-in">
              <CheckCircle className="text-white w-11 h-11 sm:w-14 sm:h-14" />
            </div>
            <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 animate-spin-slow">
              <Sparkles className="w-4 h-4 text-yellow-500 sm:w-5 sm:h-5" />
            </div>
            <div className="absolute -bottom-1 -left-1 sm:-bottom-2 sm:-left-2 animate-spin-slow-reverse">
              <Star className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-yellow-500" />
            </div>
          </div>

          {/* Celebration Text - Responsive typography */}
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center justify-center gap-2 sm:gap-3 animate-slide-up">
              <PartyPopper className="w-6 h-6 sm:w-8 sm:h-8 text-primary animate-bounce" />
              <h1 className="text-2xl font-bold text-transparent sm:text-4xl md:text-5xl bg-gradient-to-r from-primary via-purple-600 to-pink-600 bg-clip-text animate-gradient">
                Congratulations!
              </h1>
              <PartyPopper className="w-6 h-6 delay-150 sm:w-8 sm:h-8 text-primary animate-bounce" />
            </div>
            
            <div className="space-y-1 delay-100 sm:space-y-2 animate-slide-up">
              <p className="text-sm text-gray-700 sm:text-lg md:text-xl">
                You've successfully enrolled in
              </p>
              {course && (
                <div className="relative inline-block max-w-full px-2">
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-purple-600/20 blur-xl" />
                  <p className="relative text-base font-bold text-transparent break-words sm:text-xl md:text-2xl bg-gradient-to-r from-primary to-purple-600 bg-clip-text">
                    {course.title}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Achievement Badges - Wrap on mobile */}
          <div className="flex flex-wrap items-center justify-center gap-2 delay-200 sm:gap-4 animate-slide-up">
            <div className="flex items-center gap-1.5 sm:gap-2 px-2 py-1 sm:px-3 sm:py-1.5 bg-white/80 backdrop-blur rounded-full shadow-md">
              <Award className="w-3 h-3 text-yellow-500 sm:w-4 sm:h-4" />
              <span className="text-[10px] sm:text-xs font-medium text-gray-700">+100 XP</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 px-2 py-1 sm:px-3 sm:py-1.5 bg-white/80 backdrop-blur rounded-full shadow-md">
              <Trophy className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
              <span className="text-[10px] sm:text-xs font-medium text-gray-700">First Enrollment</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 px-2 py-1 sm:px-3 sm:py-1.5 bg-white/80 backdrop-blur rounded-full shadow-md">
              <Gift className="w-3 h-3 text-pink-500 sm:w-4 sm:h-4" />
              <span className="text-[10px] sm:text-xs font-medium text-gray-700">Welcome Bonus</span>
            </div>
          </div>

          {/* Motivational Message */}
          <div className="p-4 delay-300 shadow-lg sm:p-5 bg-white/60 backdrop-blur-sm rounded-xl sm:rounded-2xl animate-slide-up">
            <p className="text-[11px] sm:text-sm italic text-gray-600">
              "The journey of a thousand miles begins with a single step. 
              We're excited to have you on board! 🚀"
            </p>
          </div>

          {/* Action Buttons - Stack on mobile */}
          <div className="flex flex-col justify-center gap-3 sm:gap-4 animate-slide-up delay-400">
            {course ? (
              <button
                onClick={() => navigate(`/course/${course._id || course.id}/learn`)}
                className="group relative px-5 py-2.5 sm:px-8 sm:py-3.5 bg-gradient-to-r from-primary to-purple-600 text-white text-xs sm:text-sm font-medium rounded-xl hover:shadow-xl transition-all duration-300 hover:scale-105 overflow-hidden"
              >
                <div className="absolute inset-0 transition-transform duration-700 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover:translate-x-full" />
                <div className="relative flex items-center justify-center gap-1.5 sm:gap-2">
                  <PlayCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                  Start Learning Now
                  <Rocket className="w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform group-hover:translate-x-1" />
                </div>
              </button>
            ) : (
              <button
                onClick={() => navigate("/courses")}
                className="group relative px-5 py-2.5 sm:px-8 sm:py-3.5 bg-gradient-to-r from-primary to-purple-600 text-white text-xs sm:text-sm font-medium rounded-xl hover:shadow-xl transition-all duration-300 hover:scale-105 overflow-hidden"
              >
                <div className="absolute inset-0 transition-transform duration-700 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover:translate-x-full" />
                <div className="relative flex items-center justify-center gap-1.5 sm:gap-2">
                  <PlayCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                  Browse More Courses
                  <Rocket className="w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform group-hover:translate-x-1" />
                </div>
              </button>
            )}
            <button
              onClick={() => navigate("/dashboard/student/courses")}
              className="px-5 py-2.5 sm:px-8 sm:py-3.5 bg-white text-gray-700 text-xs sm:text-sm font-medium rounded-xl border border-gray-200 hover:bg-gray-50 hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-1.5 sm:gap-2"
            >
              <Home className="w-4 h-4 sm:w-5 sm:h-5" />
              View Enrolled Courses
            </button>
          </div>

          {/* Share Section - Optimized for mobile */}
          <div className="pt-4 delay-500 sm:pt-6 animate-slide-up">
            <p className="mb-2 sm:mb-3 text-[10px] sm:text-xs text-gray-500">Share your achievement</p>
            <div className="flex items-center justify-center gap-2 sm:gap-3">
              <button className="p-1.5 sm:p-2 bg-[#1877F2] text-white rounded-full hover:scale-110 transition-transform">
                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </button>
              <button className="p-1.5 sm:p-2 bg-[#1DA1F2] text-white rounded-full hover:scale-110 transition-transform">
                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 0021.45-8.01c.59-.016 1.175-.016 1.765-.016 1.084 0 2.15-.16 3.15-.47a9.86 9.86 0 01-2.25 2.33z"/>
                </svg>
              </button>
              <button className="p-1.5 sm:p-2 bg-[#0077B5] text-white rounded-full hover:scale-110 transition-transform">
                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451c.98 0 1.771-.773 1.771-1.729V1.729C24 .774 23.203 0 22.225 0z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes confetti {
          0% {
            transform: translateY(-100vh) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-15px) rotate(5deg);
          }
        }

        @keyframes firework {
          0% {
            transform: scale(0);
            opacity: 1;
          }
          100% {
            transform: scale(2);
            opacity: 0;
          }
        }

        @keyframes explode {
          0% {
            transform: rotate(0deg) translateX(0px);
            opacity: 1;
          }
          100% {
            transform: rotate(0deg) translateX(25px);
            opacity: 0;
          }
        }

        @keyframes bounce-in {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.15);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes slide-up {
          0% {
            transform: translateY(20px);
            opacity: 0;
          }
          100% {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes spin-slow-reverse {
          from {
            transform: rotate(360deg);
          }
          to {
            transform: rotate(0deg);
          }
        }

        .animate-confetti {
          animation: confetti linear forwards;
        }
        .animate-float {
          animation: float ease-in-out infinite;
        }
        .animate-firework {
          animation: firework 0.8s ease-out forwards;
        }
        .animate-explode {
          animation: explode 0.4s ease-out forwards;
        }
        .animate-bounce-in {
          animation: bounce-in 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards;
        }
        .animate-slide-up {
          animation: slide-up 0.5s ease-out forwards;
          opacity: 0;
        }
        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 3s linear infinite;
        }
        .animate-spin-slow {
          animation: spin-slow 4s linear infinite;
        }
        .animate-spin-slow-reverse {
          animation: spin-slow-reverse 4s linear infinite;
        }
        .delay-100 { animation-delay: 0.1s; }
        .delay-150 { animation-delay: 0.15s; }
        .delay-200 { animation-delay: 0.2s; }
        .delay-300 { animation-delay: 0.3s; }
        .delay-400 { animation-delay: 0.4s; }
        .delay-500 { animation-delay: 0.5s; }
        .delay-1000 { animation-delay: 1s; }
        .delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
};

export default EnrollmentSuccessPage;